<template>
  <v-card class="secondary" height="100%">
    <v-form ref="form">
      <v-card-title class="font-weight-medium secondary--text primary">
        {{ edit ? "Edit Term Course" : "Add Term Course" }}
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('closeDialog')">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-5">
        <v-row>
          <template v-if="!(isStream && form.type === 'Elective')">
            <v-col cols="4">
              <v-text-field
                v-model.trim="form.order_sequence"
                label="Order*"
                outlined
                dense
                readonly
                hide-details="auto"
                :rules="[rules.required, rules.number]"
              />
            </v-col>

            <v-col cols="4">
              <v-text-field
                v-model.trim="form.credits"
                label="Credits*"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required, rules.number]"
              />
            </v-col>
          </template>

          <v-col :cols="isStream && form.type === 'Elective' ? 12 : 4">
            <v-autocomplete
              v-if="!lockedStreamName"
              v-model="form.type"
              :items="allowedTypes || []"
              label="Type*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
              @change="onTypeChange"
            />
            <v-text-field
              v-else
              :value="`Elective — ${lockedStreamName}`"
              label="Type*"
              outlined
              dense
              readonly
              hide-details="auto"
            />
          </v-col>

          <template v-if="form.type === 'Core'">
            <v-col cols="8">
              <v-autocomplete
                v-model="coreEntry.course_id"
                :items="allowedCoreCourses || []"
                outlined
                dense
                label="Course*"
                :rules="[rules.required]"
                :item-text="(c) => `${c.name} - ${c.code}`"
                item-value="id"
                hide-details="auto"
                :search-input.sync="coreSearch"
                @change="coreSearch = null"
              />
            </v-col>
            <v-col cols="4">
              <v-autocomplete
                v-model="coreEntry.assessment_scheme_id"
                label="Assessment*"
                :items="assessment_schemes || []"
                item-text="name"
                item-value="id"
                outlined
                dense
                :rules="[rules.required]"
                hide-details="auto"
              />
            </v-col>
          </template>

          <template v-if="form.type === 'Elective' && !isStream">
            <v-col cols="12">
              <v-autocomplete
                v-model="selectedCourseIds"
                :items="allowedElectiveCourses || []"
                :item-text="(c) => `${c.name} - ${c.code}`"
                item-value="id"
                label="Select Courses*"
                chips
                small-chips
                outlined
                multiple
                dense
                hide-details="auto"
                :rules="[rules.required]"
                :search-input.sync="electiveSearch"
                @change="electiveSearch = null; onGroupCourseChange($event)"
              >
                <template v-slot:selection="{ item, index }">
                  <template v-if="index < 3">
                    <v-chip small class="ma-1" outlined>
                      {{ item.name }} - {{ item.code }}
                    </v-chip>
                  </template>
                  <template v-else-if="index === 3">
                    <span class="ma-1 text-caption">
                      +{{ selectedCourseIds.length - 3 }} other{{
                        selectedCourseIds.length - 3 > 1 ? "s" : ""
                      }}
                    </span>
                  </template>
                </template>
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-if="item" v-on="on" v-bind="attrs">
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="selectedCourseIds.includes(item.id)"
                        color="accent"
                        dense
                        hide-details
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title
                        >{{ item.name }} - {{ item.code }}</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" v-if="selectedCourseIds.length">
              <div class="text-caption font-weight-medium mb-2 accent--text">
                Assessment Scheme per Course
              </div>
              <v-data-table
                :headers="assessmentTableHeaders"
                :items="groupTableItems"
                hide-default-footer
                dense
                class="elevation-1"
              >
                <template v-slot:item.course="{ item }">
                  <div>
                    <span class="font-weight-medium text-caption">{{
                      item.courseName
                    }}</span>
                    <div class="text-caption grey--text">
                      {{ item.courseCode }}
                    </div>
                  </div>
                </template>
                <template v-slot:item.assessment="{ item }">
                  <v-autocomplete
                    :value="electiveAssessments[item.courseId]"
                    @input="setAssessment(item.courseId, $event)"
                    :items="assessment_schemes || []"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                    placeholder="Select assessment"
                    class="mt-0 pt-0"
                    clearable
                    outlined
                  />
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip
                    small
                    :color="
                      electiveAssessments[item.courseId] ? 'success' : 'warning'
                    "
                    text-color="white"
                  >
                    {{
                      electiveAssessments[item.courseId]
                        ? "Configured"
                        : "Pending"
                    }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-col>
          </template>

          <template v-if="form.type === 'Elective' && isStream">
            <v-col cols="12">
              <v-autocomplete
                v-model="selectedCourseIds"
                :items="allowedElectiveCourses || []"
                :item-text="(c) => `${c.name} - ${c.code}`"
                item-value="id"
                :label="`Select exactly ${requiredStreamCount} course(s) for ${lockedStreamName}*`"
                chips
                small-chips
                outlined
                multiple
                dense
                hide-details="auto"
                :rules="[rules.required, streamCountRule]"
                :search-input.sync="streamSearch"
                @change="streamSearch = null; onStreamCourseChange($event)"
              >
                <template v-slot:selection="{ item, index }">
                  <template v-if="index < 3">
                    <v-chip small class="ma-1" outlined>
                      {{ item.name }} - {{ item.code }}
                    </v-chip>
                  </template>
                  <template v-else-if="index === 3">
                    <span class="ma-1 text-caption">
                      +{{ selectedCourseIds.length - 3 }} other{{
                        selectedCourseIds.length - 3 > 1 ? "s" : ""
                      }}
                    </span>
                  </template>
                </template>
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-if="item" v-on="on" v-bind="attrs">
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="selectedCourseIds.includes(item.id)"
                        color="accent"
                        dense
                        hide-details
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title
                        >{{ item.name }} - {{ item.code }}</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <v-alert
                v-if="
                  selectedCourseIds.length > 0 &&
                  selectedCourseIds.length !== requiredStreamCount
                "
                type="warning"
                dense
                outlined
                class="mt-2 mb-0 caption"
              >
                Select exactly {{ requiredStreamCount }} course(s) for stream
                "{{ lockedStreamName }}". Currently selected:
                {{ selectedCourseIds.length }}.
              </v-alert>
            </v-col>

            <v-col cols="12" v-if="selectedCourseIds.length">
              <div class="text-caption font-weight-medium mb-2 accent--text">
                Order, Credits & Assessment per Course
              </div>
              <v-data-table
                :headers="streamTableHeaders"
                :items="groupTableItems"
                hide-default-footer
                dense
                class="elevation-1"
              >
                <template v-slot:item.course="{ item }">
                  <div>
                    <span class="font-weight-medium text-caption">{{
                      item.courseName
                    }}</span>
                    <div class="text-caption grey--text">
                      {{ item.courseCode }}
                    </div>
                  </div>
                </template>
                <template v-slot:item.order="{ item }">
                  <span class="text-caption">{{
                    streamCourseOrder(item.courseId)
                  }}</span>
                </template>
                <template v-slot:item.credits="{ item }">
                  <v-text-field
                    :value="
                      courseOrderCredits[item.courseId] &&
                      courseOrderCredits[item.courseId].credits
                    "
                    @input="setCourseCredits(item.courseId, $event)"
                    type="number"
                    dense
                    hide-details
                    outlined
                    placeholder="Credits"
                    class="mt-0 pt-0"
                  />
                </template>
                <template v-slot:item.assessment="{ item }">
                  <v-autocomplete
                    :value="electiveAssessments[item.courseId]"
                    @input="setAssessment(item.courseId, $event)"
                    :items="assessment_schemes || []"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                    placeholder="Select assessment"
                    class="mt-0 pt-0"
                    clearable
                    outlined
                  />
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip
                    small
                    :color="
                      courseRowComplete(item.courseId) ? 'success' : 'warning'
                    "
                    text-color="white"
                  >
                    {{
                      courseRowComplete(item.courseId)
                        ? "Configured"
                        : "Pending"
                    }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-col>
          </template>

          <v-col cols="6" v-if="partner_options.length">
            <v-radio-group
              v-model="form.partner"
              :disabled="partner_options.length === 1"
              class="pt-0 mt-2"
              label="Handled By*"
              :rules="[rules.required]"
              row
            >
              <v-radio
                v-for="p in partner_options"
                :key="p.id"
                :label="p.name"
                :value="p"
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pt-0 mb-3 mx-2">
        <v-spacer></v-spacer>
        <v-btn class="primary" @click="save">
          {{ edit ? "Update" : "Save" }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  props: {
    pst_course: { type: Object, default: () => ({}) },
    program_scheme_term: { type: Object, default: () => ({}) },
    program_scheme: { type: Object, default: () => ({}) },
    edit: { type: Boolean, default: false },
    upcomingOrder: { type: Number, default: 0 },
    pst_courses: { type: Array, default: () => [] },
    lockedStreamName: { type: String, default: null },
  },

  data() {
    return {
      rules,
      form: {
        order_sequence: null,
        partner: null,
        partner_id: null,
        type: null,
        credits: null,
        program_scheme_term_id: null,
        stream_name: null,
      },
      coreEntry: { course_id: null, assessment_scheme_id: null },
      selectedCourseIds: [],
      electiveAssessments: {},
      courseOrderCredits: {},
      allCourses: [],
      isLoading: false,
      partners: [],
      partner_options: [],
      assessment_schemes: [],
      coreSearch: null,
      electiveSearch: null,
      streamSearch: null,
    };
  },

  computed: {
    isStream() {
      return !!this.program_scheme_term?.is_stream;
    },

    requiredStreamCount() {
      return Number(this.program_scheme_term.no_of_electives) || 0;
    },

    assessmentTableHeaders() {
      return [
        { text: "Course", value: "course", width: "40%", sortable: false },
        {
          text: "Assessment Scheme",
          value: "assessment",
          width: "40%",
          sortable: false,
        },
        {
          text: "Status",
          value: "status",
          width: "20%",
          align: "center",
          sortable: false,
        },
      ];
    },

    streamTableHeaders() {
      return [
        { text: "Course", value: "course", width: "30%", sortable: false },
        { text: "Order", value: "order", width: "12%", sortable: false },
        { text: "Credits", value: "credits", width: "12%", sortable: false },
        {
          text: "Assessment Scheme",
          value: "assessment",
          width: "30%",
          sortable: false,
        },
        {
          text: "Status",
          value: "status",
          width: "16%",
          align: "center",
          sortable: false,
        },
      ];
    },

    groupTableItems() {
      return this.selectedCourseIds.map((cid) => {
        const course = this.allCourses.find((c) => c.id === cid);
        return {
          courseId: cid,
          courseName: course?.name || `#${cid}`,
          courseCode: course?.code || "",
        };
      });
    },

    courseCounts() {
      const core = this.pst_courses.filter((c) => c.type === "Core").length;
      const elective = this.pst_courses.filter(
        (c) => c.type === "Elective",
      ).length;
      return { core, elective };
    },

    allowedTypes() {
      if (this.isStream) {
        if (!this.edit) {
          const { no_of_courses } = this.program_scheme_term;
          const { core } = this.courseCounts;
          return core < no_of_courses ? ["Core"] : [];
        }
        return ["Core"];
      }

      if (!this.edit) {
        const { no_of_courses, no_of_electives } = this.program_scheme_term;
        const { core, elective } = this.courseCounts;
        const allowed = [];
        if (core < no_of_courses) allowed.push("Core");
        if (elective < no_of_electives) allowed.push("Elective");
        return allowed;
      }
      return ["Core", "Elective"];
    },

    allowedCoreCourses() {
      const usedIds = this.pst_courses.flatMap((c) =>
        (c.courses || []).map((e) => e.course_id),
      );
      if (!this.edit) {
        return this.allCourses.filter(
          (c) => c.active && !usedIds.includes(c.id),
        );
      }
      return this.allCourses.filter(
        (c) => c.active || c.id === this.coreEntry.course_id,
      );
    },

    allowedElectiveCourses() {
      if (this.isStream) {
        if (!this.edit) return this.allCourses.filter((c) => c.active);
        return this.allCourses.filter(
          (c) => c.active || this.selectedCourseIds.includes(c.id),
        );
      }

      const usedIdsElsewhere = this.pst_courses
        .filter((c) => c.type === "Elective" && c.id !== this.pst_course?.id)
        .flatMap((c) => (c.courses || []).map((e) => e.course_id));

      if (!this.edit) {
        return this.allCourses.filter(
          (c) => c.active && !usedIdsElsewhere.includes(c.id),
        );
      }
      return this.allCourses.filter(
        (c) =>
          (c.active || this.selectedCourseIds.includes(c.id)) &&
          !usedIdsElsewhere.includes(c.id),
      );
    },
  },

  watch: {
    partner_options(newVal) {
      if (newVal.length === 1) {
        this.form.partner = newVal[0];
      } else if (newVal.length > 1 && !this.edit) {
        this.form.partner = null;
      }
    },

    allowedTypes: {
      immediate: true,
      handler(newVal) {
        if (
          !this.edit &&
          !this.lockedStreamName &&
          (newVal || []).length === 1
        ) {
          this.form.type = newVal[0];
        }
      },
    },
  },

  async created() {
    await this.init();
    this.form.program_scheme_term_id = this.program_scheme_term.id;
  },

  methods: {
    streamCountRule() {
      if (!this.isStream) return true;
      return (
        this.selectedCourseIds.length === this.requiredStreamCount ||
        `Select exactly ${this.requiredStreamCount} course(s)`
      );
    },

    onStreamCourseChange(ids) {
      if (ids.length > this.requiredStreamCount) {
        this.$store.commit("showSnackbar", {
          message: `Only ${this.requiredStreamCount} course(s) allowed for stream "${this.lockedStreamName}".`,
          color: "warning",
        });
        this.selectedCourseIds = ids.slice(0, this.requiredStreamCount);
      }
      Object.keys(this.electiveAssessments).forEach((key) => {
        if (!this.selectedCourseIds.includes(parseInt(key))) {
          this.$delete(this.electiveAssessments, key);
        }
      });
      Object.keys(this.courseOrderCredits).forEach((key) => {
        if (!this.selectedCourseIds.includes(parseInt(key))) {
          this.$delete(this.courseOrderCredits, key);
        }
      });
      this.selectedCourseIds.forEach((cid) => {
        if (!this.courseOrderCredits[cid]) {
          this.$set(this.courseOrderCredits, cid, { credits: null });
        }
      });
    },

    streamCourseOrder(courseId) {
      const idx = this.selectedCourseIds.indexOf(courseId);
      if (idx === -1) return null;
      return this.upcomingOrder + idx;
    },

    setCourseCredits(courseId, value) {
      const existing = this.courseOrderCredits[courseId] || {};
      this.$set(this.courseOrderCredits, courseId, {
        ...existing,
        credits: value,
      });
    },

    courseRowComplete(courseId) {
      const oc = this.courseOrderCredits[courseId];
      return !!(
        this.electiveAssessments[courseId] &&
        oc &&
        oc.credits !== null &&
        oc.credits !== ""
      );
    },

    onGroupCourseChange(ids) {
      Object.keys(this.electiveAssessments).forEach((key) => {
        if (!ids.includes(parseInt(key))) {
          this.$delete(this.electiveAssessments, key);
        }
      });
    },

    setAssessment(key, value) {
      this.$set(this.electiveAssessments, key, value);
    },

    onTypeChange() {
      this.coreEntry = { course_id: null, assessment_scheme_id: null };
      this.selectedCourseIds = [];
      this.electiveAssessments = {};
      this.courseOrderCredits = {};
      this.coreSearch = null;
      this.electiveSearch = null;
      this.streamSearch = null;
    },

    async init() {
      await Promise.all([
        this.fetchPartners(),
        this.fetchCourses(),
        this.getAssessment(),
      ]);
      this.getPartner();

      if (this.edit) {
        const pc = this.pst_course;
        this.form = {
          id: pc.id,
          order_sequence: pc.order_sequence,
          credits: pc.credits,
          type: pc.type,
          partner_id: pc.partner_id,
          partner: this.partners.find((p) => p.id == pc.partner_id) || null,
          program_scheme_term_id: pc.program_scheme_term_id,
          stream_name: pc.stream_name || this.lockedStreamName || null,
        };

        const entries = pc.courses || [];

        if (pc.type === "Core") {
          this.coreEntry = {
            course_id: entries[0]?.course_id || null,
            assessment_scheme_id: entries[0]?.assessment_scheme_id || null,
          };
        } else if (pc.type === "Elective") {
          this.selectedCourseIds = entries.map((e) => e.course_id);
          entries.forEach((e) => {
            if (e.assessment_scheme_id) {
              this.$set(
                this.electiveAssessments,
                e.course_id,
                e.assessment_scheme_id,
              );
            }
            if (this.isStream) {
              this.$set(this.courseOrderCredits, e.course_id, {
                credits: e.credits ?? null,
              });
            }
          });
        }
      } else {
        this.form.order_sequence = this.upcomingOrder;

        if (this.lockedStreamName) {
          this.form.type = "Elective";
          this.form.stream_name = this.lockedStreamName;
        }
      }
    },

    save() {
      if (!this.$refs.form.validate()) return;

      let courses = [];

      if (this.form.type === "Core") {
        if (!this.coreEntry.course_id || !this.coreEntry.assessment_scheme_id) {
          this.$store.commit("showSnackbar", {
            message: "Please select a course and assessment scheme",
            color: "error",
          });
          return;
        }
        courses = [
          {
            course_id: this.coreEntry.course_id,
            assessment_scheme_id: this.coreEntry.assessment_scheme_id,
          },
        ];
        this.form.stream_name = null;
      } else if (this.form.type === "Elective" && this.isStream) {
        if (this.selectedCourseIds.length !== this.requiredStreamCount) {
          this.$store.commit("showSnackbar", {
            message: `Stream "${this.lockedStreamName}" must have exactly ${this.requiredStreamCount} course(s).`,
            color: "error",
          });
          return;
        }
        for (const cid of this.selectedCourseIds) {
          const course = this.allCourses.find((c) => c.id === cid);
          if (!this.electiveAssessments[cid]) {
            this.$store.commit("showSnackbar", {
              message: `Please select assessment scheme for ${
                course?.name || cid
              }`,
              color: "error",
            });
            return;
          }
          const oc = this.courseOrderCredits[cid];
          if (!oc || oc.credits === null || oc.credits === "") {
            this.$store.commit("showSnackbar", {
              message: `Please enter credits for ${course?.name || cid}`,
              color: "error",
            });
            return;
          }
        }
        courses = this.selectedCourseIds.map((cid) => ({
          course_id: cid,
          assessment_scheme_id: this.electiveAssessments[cid],
          order_sequence: this.streamCourseOrder(cid),
          credits: Number(this.courseOrderCredits[cid].credits),
          stream_name: this.lockedStreamName,
        }));

        const streamTarget = Number(this.program_scheme_term.stream_credits);
        if (streamTarget) {
          const creditsSum = courses.reduce((sum, c) => sum + c.credits, 0);
          if (creditsSum !== streamTarget) {
            this.$store.commit("showSnackbar", {
              message: `Credits for stream "${this.lockedStreamName}" must total ${streamTarget} (currently ${creditsSum}).`,
              color: "error",
            });
            return;
          }
        }

        this.form.stream_name = this.lockedStreamName;

        if (!this.edit) this.form.order_sequence = this.upcomingOrder;
        this.form.credits = null;
      } else if (this.form.type === "Elective") {
        if (this.selectedCourseIds.length === 0) {
          this.$store.commit("showSnackbar", {
            message: "Please select at least one course",
            color: "error",
          });
          return;
        }
        for (const cid of this.selectedCourseIds) {
          if (!this.electiveAssessments[cid]) {
            const course = this.allCourses.find((c) => c.id === cid);
            this.$store.commit("showSnackbar", {
              message: `Please select assessment scheme for ${
                course?.name || cid
              }`,
              color: "error",
            });
            return;
          }
        }
        courses = this.selectedCourseIds.map((cid) => ({
          course_id: cid,
          assessment_scheme_id: this.electiveAssessments[cid],
        }));
        this.form.stream_name = null;
      }

      this.form.partner_id = String(this.form.partner.id);

      const submitData = {
        ...this.form,
        courses,
        partner: undefined,
      };

      this.$emit(this.edit ? "update" : "create", submitData);
    },

    async getAssessment() {
      try {
        const res = await this.$axios.get("/assessment_schemes");
        this.assessment_schemes = res.data.data || [];
      } catch {
        this.assessment_schemes = [];
      }
    },

    async fetchPartners() {
      try {
        const res = await this.$axios.get("/partners");
        this.partners = (res.data.data || []).sort((a, b) => a.id - b.id);
      } catch {
        this.partners = [];
      }
    },

    async fetchCourses() {
      this.isLoading = true;
      try {
        const res = await this.$axios.get("/courses");
        this.allCourses = res.data.data || [];
      } catch (error) {
        this.allCourses = [];
        const msg = await this.$store.dispatch("throwError", error.response);
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      }
      this.isLoading = false;
    },

    getPartner() {
      const scheme = { ...this.program_scheme };
      const mainPartner = scheme.partner_id
        ? this.partners.find(
            (p) => p.id === scheme.partner_id && p.code !== "SAS",
          )
        : null;
      const sasPartner = this.partners.find((p) => p.code === "SAS");
      this.partner_options = mainPartner
        ? [mainPartner, sasPartner].filter(Boolean)
        : [sasPartner].filter(Boolean);
      this.form.partner =
        this.partner_options.length === 1 ? this.partner_options[0] : null;
    },
  },
};
</script>

<style scoped>
::v-deep .v-data-table td {
  padding: 8px 16px;
}
::v-deep .v-autocomplete .v-input__control {
  min-height: 40px;
}
.v-input {
  font-size: 12px !important;
}
</style>